import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Observable, Subscription } from 'rxjs';
import { I18nService } from 'i18n';
import { FoundingMemberBadgeComponent } from 'shared';
import { CommunityService } from '../../../services/community.service';
import {
  CommunityChannel,
  CommunityGroup,
  CommunityPost,
  CommunityPostNode,
  CommunitySanction,
  CommunityThread,
  communityErrorKey,
  communityBodyBlank
} from '../../../models/community.models';
import { CommunityEditorComponent } from '../../../components/community-editor/community-editor.component';
import { CommunityHistoryComponent } from '../../../components/community-history/community-history.component';
import { CommunityBadgesComponent } from '../../../components/community-badges/community-badges.component';
import { CommunityReactionsComponent } from '../../../components/community-reactions/community-reactions.component';
import {
  CommunityPostComponent,
  PostEditPayload,
  PostHideUserPayload,
  PostReplyPayload
} from '../../../components/community-post/community-post.component';
import { CommunityAvatarComponent } from '../../../components/community-avatar/community-avatar.component';
import { CommunityReasonModalComponent } from '../../../components/community-reason-modal/community-reason-modal.component';
import { CommunityMemberSummaryComponent } from '../../../components/community-member-summary/community-member-summary.component';
import { CommunitySanctionNoticeComponent } from '../../../components/community-sanction-notice/community-sanction-notice.component';
import { CommunityBodyPipe } from '../../../pipes/community-body.pipe';
import { CommunityVideoEmbedsDirective } from '../../../components/community-video-embed/community-video-embeds.directive';
import { CommunityTranslateComponent } from '../../../components/community-translate/community-translate.component';
import {
  CommunityThreadTranslationService,
  CommunityTranslationTarget
} from '../../../services/community-thread-translation.service';
import { CommunityTranslationPreferenceService } from '../../../services/community-translation-preference.service';
import { CommunityLanguageCheckService } from '../../../services/community-language-check.service';
import { CommunityLanguageConfirmModalComponent } from '../../../components/community-language-confirm-modal/community-language-confirm-modal.component';

/** What the open report dialog is about. */
interface ReportTarget {
  type: 'CommunityThread' | 'CommunityPost';
  id: number;
}

/** A group with only the channels the viewer may move threads into. */
interface MoveTargetGroup {
  group: CommunityGroup;
  channels: CommunityChannel[];
}

@Component({
  selector: 'app-community-thread-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslocoModule,
    CommunityEditorComponent,
    CommunityHistoryComponent,
    CommunityPostComponent,
    FoundingMemberBadgeComponent,
    CommunityBadgesComponent,
    CommunityReactionsComponent,
    CommunityAvatarComponent,
    CommunityReasonModalComponent,
    CommunityMemberSummaryComponent,
    CommunitySanctionNoticeComponent,
    CommunityBodyPipe,
    CommunityVideoEmbedsDirective,
    CommunityTranslateComponent,
    CommunityLanguageConfirmModalComponent
  ],
  templateUrl: './community-thread-detail-page.component.html',
  styleUrl: './community-thread-detail-page.component.scss',
  // One coordinator per page: the whole thread is translated in one call, and a
  // fresh page must start with an empty map (§3.2).
  providers: [CommunityThreadTranslationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityThreadDetailPageComponent implements OnInit, OnDestroy {
  thread = signal<CommunityThread | null>(null);
  posts = signal<CommunityPost[]>([]);
  postTree = computed<CommunityPostNode[]>(() => this.communityService.buildTree(this.posts()));
  loading = signal(false);
  /** Translation key of the last failed action (403 codes etc.). */
  actionError = signal<string | null>(null);
  /** Set when the thread itself could not be loaded. */
  loadError = signal<string | null>(null);

  replyOpen = signal(false);
  replyDraft = '';

  /**
   * On while a save is in flight, the language check (§7.3) included — the check
   * runs before the request goes out, and the send buttons must stay disabled for
   * the whole of it, not just for the part after the dialog.
   */
  submitting = signal(false);

  editThreadOpen = signal(false);
  editTitleDraft = '';
  editBodyDraft = '';

  historyOpen = signal(false);

  /**
   * The channel's reaction set. The thread's channel brief does not carry it, so
   * it is fetched once per channel; until then the bar shows the default keys.
   */
  reactionSet = signal<string[] | null>(null);
  /**
   * Full channel rights, fetched once per channel — the thread's channel brief
   * carries neither `permissions` nor `allowed_uploads`, both needed to gate
   * the editor's mention/upload/embed extras.
   */
  channelPermissions = signal<CommunityChannel['permissions'] | null>(null);
  channelAllowedUploads = signal<string[]>([]);
  /** Private Room flag from the full channel (the thread's channel brief may not carry it). */
  channelPrivate = signal(false);
  /** Participants of a Private Room in read-only mode (from the full channel). */
  channelParticipantsReadOnly = signal(false);
  canMentionHere = computed(() => this.channelPermissions()?.mention === true);
  canUploadImageHere = computed(() => this.channelPermissions()?.upload_image === true && this.channelAllowedUploads().includes('image'));
  canUploadFileHere = computed(() => this.channelPermissions()?.upload_file === true && this.channelAllowedUploads().includes('file'));
  canEmbedVideoHere = computed(() => this.channelPermissions()?.embed_video === true && this.channelAllowedUploads().includes('video_link'));
  private channelDetailsChannelId: number | null = null;

  /** Overview groups, loaded only for moderators (move targets). */
  /**
   * Whether this thread lives in a Private Room: from the thread's channel
   * brief, the full channel, or the overview -- whichever answers first.
   */
  isPrivateRoom = computed<boolean>(() => {
    const th = this.thread();
    if (!th) return false;
    if (th.channel?.private === true || this.channelPrivate()) return true;
    return this.groups().some(g => g.channels.some(c => c.id === th.channel_id && c.private === true));
  });

  /**
   * Copy protection in Private Rooms (as far as practicable): no context menu on
   * the posts -- except inside the viewer's own input fields.
   */
  blockContextMenu(event: Event): void {
    if (!this.isPrivateRoom()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, [contenteditable="true"], app-community-editor')) return;
    event.preventDefault();
  }

  groups = signal<CommunityGroup[]>([]);
  /** Whether the viewer is muted network-wide (from the overview). */
  muted = signal(false);
  /** What is in force against the viewer (from the overview), shown next to the no-reply notice. */
  sanctions = signal<CommunitySanction[]>([]);
  warnings = signal<CommunitySanction[]>([]);

  // --- Reports (§22) ---------------------------------------------------------
  reportTarget = signal<ReportTarget | null>(null);
  reportBusy = signal(false);
  reportError = signal<string | null>(null);
  /** Outcome of the last report, shown as a dismissible notice. */
  reportNotice = signal<'created' | 'existing' | null>(null);

  // --- Mute/unmute an author (reason mandatory since Phase 2) -----------------
  hideUserTarget = signal<PostHideUserPayload | null>(null);
  hideUserBusy = signal(false);
  hideUserError = signal<string | null>(null);

  /** Author whose moderation summary is open. */
  summaryUserId = signal<number | null>(null);

  /** Whether the network has a translation provider configured (§15, F10); from the overview. */
  translationAvailable = signal(false);

  /**
   * The thread's translated title while its translation is on screen; null
   * whenever the original is showing, so the heading falls back to `th.title`.
   */
  translatedTitle = computed<string | null>(() => {
    const entry = this.translations.entryFor('thread', this.threadId);
    return entry?.status === 'ready' && entry.showing ? entry.title ?? null : null;
  });

  // --- Q&A (§17): best-answer marking, `qa` channels only --------------------
  isQaChannel = computed<boolean>(() => this.thread()?.channel?.mode === 'qa');
  canSolve = computed<boolean>(() => this.isQaChannel() && this.thread()?.can_solve === true);
  solvedPost = computed<CommunityPost | null>(() => {
    const id = this.thread()?.solved_post_id;
    return id ? this.posts().find(p => p.id === id) ?? null : null;
  });

  /** Pending/rejected badge on the thread: for its author and for moderators. */
  approvalBadge = computed<'pending' | 'rejected' | null>(() => {
    const th = this.thread();
    const status = th?.approval_status;
    if (!th || (status !== 'pending' && status !== 'rejected')) return null;
    return th.is_mine || this.canModerate() ? status : null;
  });

  /** Reporting needs visible, live content by someone else. */
  canReportThread = computed<boolean>(() => {
    const th = this.thread();
    return !!th && !th.is_mine && !th.masked && !th.deleted_at && !th.hidden_at;
  });

  /** Moderation rights on this thread's channel; `can_admin` is the pre-Phase-0 alias. */
  canModerate = computed<boolean>(() => {
    const th = this.thread();
    return !!th && (th.can_moderate ?? th.can_admin ?? false);
  });

  canPin = computed<boolean>(() => {
    const th = this.thread();
    return !!th && (th.can_pin ?? this.canModerate());
  });

  canDelete = computed<boolean>(() => {
    const th = this.thread();
    return !!th && (th.can_delete ?? th.can_modify);
  });

  /**
   * Whether the viewer may comment. Prefers the backend's `can_reply` (which
   * covers read-only/archived channels, locked threads and muted users) and
   * falls back to the old locked-thread rule on backends that do not send it.
   */
  canReply = computed<boolean>(() => {
    const th = this.thread();
    if (!th) return false;
    if (th.can_reply !== undefined) return th.can_reply;
    return !th.locked || this.canModerate();
  });

  /** Translation key explaining why replying is not possible. */
  noReplyNoticeKey = computed<string>(() => {
    const th = this.thread();
    if (!th) return 'community.thread.locked_notice';
    if (th.channel?.archived) return 'community.channel.archive_notice';
    if (th.channel?.read_only) return 'community.channel.read_only_notice';
    if (th.locked) return 'community.thread.locked_notice';
    if (this.muted()) return 'community.users.muted_notice';
    return 'community.errors.community_no_permission';
  });

  /** Channels the viewer moderates, grouped, minus the thread's current one. */
  moveTargets = computed<MoveTargetGroup[]>(() => {
    const current = this.thread()?.channel_id;
    return this.groups()
      .filter(g => !g.locked)
      .map(g => ({
        group: g,
        channels: g.channels.filter(c => c.permissions.moderate && c.id !== current && !c.archived)
      }))
      .filter(x => x.channels.length > 0);
  });

  authorHidden = computed<boolean>(() => {
    const th = this.thread();
    return !!th?.author_hidden || !!th?.user?.community_hidden;
  });

  canHideAuthor = computed<boolean>(() => this.canModerate() && !!this.thread()?.user?.can_hide);

  toggleHistory(): void {
    this.historyOpen.update(v => !v);
  }

  threadId!: number;
  private routeSub?: Subscription;
  private overviewLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private communityService: CommunityService,
    private i18n: I18nService,
    private translations: CommunityThreadTranslationService,
    private translationPrefs: CommunityTranslationPreferenceService,
    private languageCheck: CommunityLanguageCheckService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.params.subscribe(params => {
      this.threadId = Number(params['id']);
      if (this.threadId) this.load();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  /** Back to the thread's channel; the "all threads" view until the thread is known. */
  get backUrl(): string {
    const channelId = this.thread()?.channel_id;
    return channelId ? `/${this.lang}/community/c/${channelId}` : `/${this.lang}/community`;
  }

  memberLink(userId: number): string {
    return `/${this.lang}/community/u/${userId}`;
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.communityService.getThread(this.threadId, this.lang).subscribe({
      next: res => {
        this.thread.set(res.thread);
        this.posts.set(res.posts);
        this.loading.set(false);
        // Since Phase 2 the channel brief carries the reaction set itself, so
        // paint the reaction bar immediately; permissions/allowed_uploads still
        // need the full channel (fetched once below, cached per channel).
        if (res.thread.channel?.reaction_set) {
          this.reactionSet.set(res.thread.channel.reaction_set);
        }
        this.loadChannelDetails(res.thread.channel_id);
        // Fetched once per page visit: the move-to-channel select and muted
        // notice need it for moderators, and every viewer needs
        // `translation_available` plus their per-language preferences.
        if (!this.overviewLoaded) {
          this.loadOverview();
        } else {
          // A reload after a reply: only the posts that are new get looked at.
          this.syncTranslations();
        }
      },
      error: err => {
        this.loading.set(false);
        this.loadError.set(err?.status === 404 ? 'community.thread.not_found' : communityErrorKey(err));
      }
    });
  }

  private loadChannelDetails(channelId: number): void {
    if (!channelId || this.channelDetailsChannelId === channelId) return;
    this.channelDetailsChannelId = channelId;
    this.communityService.getChannel(channelId, this.lang).subscribe({
      next: res => {
        this.reactionSet.set(res.channel.reaction_set ?? null);
        this.channelPermissions.set(res.channel.permissions);
        this.channelAllowedUploads.set(res.channel.allowed_uploads ?? []);
        this.channelPrivate.set(res.channel.private === true);
        this.channelParticipantsReadOnly.set(res.channel.participants_read_only === true);
      },
      error: () => {}
    });
  }

  onReactionFailed(key: string): void {
    this.actionError.set(key);
  }

  onEditorUploadError(key: string): void {
    this.actionError.set(key);
  }

  private loadOverview(): void {
    this.overviewLoaded = true;
    this.communityService.getOverview(this.lang).subscribe({
      next: res => {
        this.groups.set(res.groups);
        this.muted.set(res.muted);
        this.sanctions.set(res.sanctions ?? []);
        this.warnings.set(res.warnings ?? []);
        this.translationAvailable.set(res.translation_available === true);
        this.translationPrefs.hydrate(res.translation_preferences);
        // Only now is it known whether a provider exists and what the viewer's
        // preferences are, so the automatic pass waits for the overview.
        this.syncTranslations();
        this.translations.requestAuto();
      },
      error: () => this.groups.set([])
    });
  }

  /**
   * Hand the coordinator the elements currently on the page (thread + every
   * post with its source language). Called after each load; the coordinator
   * itself decides what is new and needs asking for.
   */
  private syncTranslations(): void {
    const th = this.thread();
    if (!th) return;
    this.translations.configure(th.id, this.translationAvailable());
    const targets: CommunityTranslationTarget[] = [{ kind: 'thread', id: th.id, sourceLocale: th.locale }];
    this.posts().forEach(post => {
      if (!post.masked) targets.push({ kind: 'post', id: post.id, sourceLocale: post.locale });
    });
    this.translations.sync(targets);
  }

  // --- Q&A (§17) ---------------------------------------------------------------

  markSolved(postId: number): void {
    this.run(this.communityService.solveThread(this.threadId, postId));
  }

  unmarkSolved(): void {
    this.run(this.communityService.unsolveThread(this.threadId));
  }

  moveToChannel(channelId: number | string | null): void {
    const id = channelId == null || channelId === '' ? null : Number(channelId);
    if (id == null || id === this.thread()?.channel_id) return;
    this.run(this.communityService.moveThread(this.threadId, id));
  }

  // Top-level reply (to the thread itself)
  openReply(): void {
    this.replyDraft = '';
    this.replyOpen.set(true);
  }

  cancelReply(): void {
    this.replyOpen.set(false);
  }

  /** A body of nothing but empty lines is not a reply — the backend refuses it too. */
  bodyBlank(html: string): boolean {
    return communityBodyBlank(html);
  }

  submitReply(): void {
    const body = (this.replyDraft || '').trim();
    if (communityBodyBlank(body)) return;
    // No `expected`: a reply inherits the thread's language, and the server is
    // the one that knows it.
    void this.runChecked({ body, threadId: this.threadId }, locale =>
      this.communityService.createPost(this.threadId, body, null, locale), () => {
        this.replyOpen.set(false);
        this.replyDraft = '';
      });
  }

  onPostReply(payload: PostReplyPayload): void {
    void this.runChecked({ body: payload.body, threadId: this.threadId }, locale =>
      this.communityService.createPost(this.threadId, payload.body, payload.parentPostId, locale));
  }

  onPostEdit(payload: PostEditPayload): void {
    // An edit is compared against the post's OWN language, not the thread's: a
    // German answer in an English thread that was already corrected once must not
    // be asked about again on every further edit.
    const current = this.posts().find(p => p.id === payload.postId)?.locale;
    void this.runChecked(
      { body: payload.body, threadId: this.threadId, expected: current },
      locale => this.communityService.updatePost(payload.postId, payload.body, locale)
    );
  }

  onPostDelete(postId: number): void {
    this.run(this.communityService.deletePost(postId));
  }

  onPostToggleHide(payload: { postId: number; hide: boolean }): void {
    this.run(this.communityService.hidePost(payload.postId, payload.hide));
  }

  /** Opens the reason dialog; the request goes out in `confirmHideUser`. */
  onToggleHideUser(payload: PostHideUserPayload): void {
    this.hideUserError.set(null);
    this.hideUserTarget.set(payload);
  }

  /** Mute/unmute the thread's author from the thread header. */
  onHideThreadAuthor(hide: boolean): void {
    const user = this.thread()?.user;
    if (!user) return;
    this.onToggleHideUser({ userId: user.id, hide, name: user.display_name });
  }

  confirmHideUser(reason: string): void {
    const target = this.hideUserTarget();
    if (!target) return;
    this.hideUserBusy.set(true);
    this.hideUserError.set(null);
    this.communityService.hideUser(target.userId, target.hide, reason).subscribe({
      next: () => {
        this.hideUserBusy.set(false);
        this.hideUserTarget.set(null);
        this.load();
      },
      error: err => {
        this.hideUserBusy.set(false);
        this.hideUserError.set(communityErrorKey(err));
      }
    });
  }

  // --- Reports ---------------------------------------------------------------

  openReport(type: 'CommunityThread' | 'CommunityPost', id: number): void {
    this.reportError.set(null);
    this.reportNotice.set(null);
    this.reportTarget.set({ type, id });
  }

  submitReport(reason: string): void {
    const target = this.reportTarget();
    if (!target) return;
    this.reportBusy.set(true);
    this.reportError.set(null);
    this.communityService.createReport(target.type, target.id, reason).subscribe({
      next: res => {
        this.reportBusy.set(false);
        this.reportTarget.set(null);
        this.reportNotice.set(res.existing ? 'existing' : 'created');
      },
      error: err => {
        this.reportBusy.set(false);
        this.reportError.set(err?.status === 404 ? 'community.report.not_found' : communityErrorKey(err));
      }
    });
  }

  openMemberSummary(userId: number | null | undefined): void {
    if (userId) this.summaryUserId.set(userId);
  }

  // Thread-level actions
  openEditThread(): void {
    const t = this.thread();
    if (!t) return;
    this.editTitleDraft = t.title || '';
    this.editBodyDraft = t.body || '';
    this.editThreadOpen.set(true);
  }

  cancelEditThread(): void {
    this.editThreadOpen.set(false);
  }

  submitEditThread(): void {
    const title = (this.editTitleDraft || '').trim();
    const body = (this.editBodyDraft || '').trim();
    if (!title || communityBodyBlank(body)) return;
    void this.runChecked(
      { title, body, threadId: this.threadId, expected: this.thread()?.locale },
      locale => this.communityService.updateThread(this.threadId, title, body, locale),
      () => this.editThreadOpen.set(false)
    );
  }

  onDeleteThread(): void {
    this.actionError.set(null);
    this.communityService.deleteThread(this.threadId).subscribe({
      next: () => this.router.navigateByUrl(this.backUrl),
      error: err => this.actionError.set(communityErrorKey(err))
    });
  }

  onPin(pin: boolean): void {
    this.run(this.communityService.pinThread(this.threadId, pin));
  }

  onLock(lock: boolean): void {
    this.run(this.communityService.lockThread(this.threadId, lock));
  }

  onHide(hide: boolean): void {
    this.run(this.communityService.hideThread(this.threadId, hide));
  }

  /** Run a mutating request, then reload; surface API error codes inline. */
  /**
   * `run`, with the language check in front of it (§7.3). A cancelled dialog ends
   * quietly — nothing is saved and no error is shown, because the writer did not
   * fail at anything, they asked to go back to their text.
   */
  private async runChecked(
    draft: { title?: string; body: string; threadId?: number; expected?: string },
    request: (locale?: string) => Observable<unknown>,
    onSuccess?: () => void
  ): Promise<void> {
    this.submitting.set(true);
    const decision = await this.languageCheck.confirmLocale(draft);
    if (!decision.proceed) {
      this.submitting.set(false);
      return;
    }
    this.actionError.set(null);
    request(decision.locale).subscribe({
      next: () => {
        this.submitting.set(false);
        onSuccess?.();
        this.load();
      },
      error: err => {
        this.submitting.set(false);
        this.actionError.set(communityErrorKey(err));
      }
    });
  }

  private run(request: Observable<unknown>, onSuccess?: () => void): void {
    this.actionError.set(null);
    request.subscribe({
      next: () => {
        onSuccess?.();
        this.load();
      },
      error: err => this.actionError.set(communityErrorKey(err))
    });
  }
}
